import { useRequestStore } from '@/features/requests/requests'
import type {
  SantaCard,
  SantaCardPayload,
  SantaMessage,
  SantaUser,
  SecretSanta,
} from '@/entities/secretSanta/model/types'

export const secretSantaApi = {
  getRequestStore() {
    return useRequestStore()
  },

  async get(): Promise<{ santa?: SecretSanta }> {
    return await this.getRequestStore().send('me/events', {
      onlyWithAuth: true,
      noCache: true,
    })
  },

  async reveal() {
    return await this.getRequestStore().send('me/events/santa/reveal', {
      method: 'POST',
      onlyWithAuth: true,
    })
  },

  async join(uuid: string): Promise<{ year: number }> {
    return await this.getRequestStore().send(`joinsanta/${encodeURIComponent(uuid)}`, {
      method: 'POST',
      onlyWithAuth: true,
    })
  },

  async markSent(): Promise<{ isSent: boolean }> {
    return await this.getRequestStore().send('me/events/santa/sent', {
      method: 'POST',
      onlyWithAuth: true,
    })
  },

  async setNotifications(enabled: boolean): Promise<{ notificationsEnabled: boolean }> {
    return await this.getRequestStore().send('me/events/santa/notifications', {
      method: 'POST',
      onlyWithAuth: true,
      payload: { enabled },
    })
  },

  async participants(): Promise<SantaUser[]> {
    return await this.getRequestStore().send('me/events/santa/participants', {
      onlyWithAuth: true,
      noCache: true,
    })
  },

  async saveCard(data: SantaCardPayload): Promise<{ card: SantaCard }> {
    if (data.newImages.length === 0) {
      return await this.getRequestStore().send('me/events/santa', {
        method: 'POST',
        onlyWithAuth: true,
        payload: {
          wishes: data.wishes,
          notToGift: data.notToGift,
          alreadyHave: data.alreadyHave,
          delivery: data.delivery,
          keepImages: data.keepImages,
        },
      })
    }

    const form = new FormData()
    form.append('wishes', data.wishes)
    form.append('notToGift', data.notToGift)
    form.append('alreadyHave', data.alreadyHave)
    form.append('delivery', JSON.stringify(data.delivery))
    if (data.keepImages.length === 0) {
      form.append('keepImages', '')
    } else {
      data.keepImages.forEach((id) => form.append('keepImages[]', String(id)))
    }
    data.newImages.forEach((file) => form.append('images[]', file))

    return await this.getRequestStore().send('me/events/santa', {
      method: 'POST',
      onlyWithAuth: true,
      payload: form,
    })
  },

  async messages(
    chatId: number,
    params: { after?: number; before?: number; limit?: number } = {},
  ): Promise<SantaMessage[]> {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) query.set(key, String(value))
    })
    const qs = query.toString()
    return await this.getRequestStore().send(`me/events/santa/chat/${chatId}${qs ? `?${qs}` : ''}`, {
      onlyWithAuth: true,
      noCache: true,
    })
  },

  async markRead(chatId: number): Promise<{ markedAsRead: number }> {
    return await this.getRequestStore().send(`me/events/santa/chat/${chatId}/read`, {
      method: 'POST',
      onlyWithAuth: true,
    })
  },

  async sendMessage(chatId: number, content: string): Promise<SantaMessage> {
    return await this.getRequestStore().send(`me/events/santa/chat/${chatId}`, {
      method: 'POST',
      onlyWithAuth: true,
      payload: { content },
    })
  },
}
